import type { Optional } from '@gov-nx/core/types';
import { is, ObjectOrArray, pipe } from '@gov-nx/core/types';
import { isFirstIndex } from '@gov-nx/utils/common';
import type { Vehicle } from '../vehicle.types';
import { getVehicleGetters } from '../vehicleUtils';
import type { RegisterSVDetailContext } from './context.types';

type VehicleDocumentsKeyValues = {
	rzKeyValues: RegisterSVDetailContext['rzKeyValues'];
	rzHistoryKeyValues: RegisterSVDetailContext['rzHistoryKeyValues'];
	tpKeyValues: RegisterSVDetailContext['tpKeyValues'];
	tpHistoryKeyValues: RegisterSVDetailContext['tpHistoryKeyValues'];
	orvKeyValues: RegisterSVDetailContext['orvKeyValues'];
	orvHistoryKeyValues: RegisterSVDetailContext['orvHistoryKeyValues'];
};

export const getVehicleDocumentsKeyValues = (
	vehicle: Optional<Vehicle>,
	t: (key: string) => string
): VehicleDocumentsKeyValues => {
	const getter = vehicle && getVehicleGetters(vehicle);

	return {
		rzKeyValues: pipe(getter?.documents('rz'), ObjectOrArray.array, (rz) =>
			rz.map((document, index) => [
				isFirstIndex(index) ? t('tabulka.dokumenty.cislo-rz') : '',
				document?.cisloRz?.xVal,
			])
		),
		rzHistoryKeyValues: pipe(
			getter?.documentsHistory('rz'),
			ObjectOrArray.array,
			(rz) =>
				rz?.map((rz, index) => [
					isFirstIndex(index) ? t('tabulka.dokumenty-historie.cislo-rz') : '',
					rz?.cisloRz?.xVal,
				])
		),
		tpKeyValues: pipe(
			getter?.documents('tp'),
			ObjectOrArray.array,
			(documents) =>
				documents.filter(
					(document) =>
						is(document?.cisloTp?.xVal) && document?.aktualni?.xVal === 'true'
				),
			(tp) =>
				tp.length
					? tp.map((document, index) => [
							isFirstIndex(index) ? t('tabulka.dokumenty.cislo-tp') : '',
							document?.cisloTp?.xVal,
						])
					: [[t('tabulka.dokumenty.cislo-tp'), undefined]]
		),
		tpHistoryKeyValues: pipe(
			getter?.documentsHistory('tp'),
			ObjectOrArray.array,
			(rz) =>
				rz?.map((rz, index) => [
					isFirstIndex(index) ? t('tabulka.dokumenty-historie.cislo-tp') : '',
					rz?.cisloTp?.xVal,
				])
		),
		orvKeyValues: pipe(getter?.documents('orv'), ObjectOrArray.array, (orvs) =>
			orvs?.map((orv, index) => [
				isFirstIndex(index) ? t('tabulka.dokumenty.cislo-orv') : '',
				orv?.cisloOrv?.xVal,
			])
		),

		orvHistoryKeyValues: pipe(
			getter?.documentsHistory('orv'),
			ObjectOrArray.array,
			(orvs) =>
				orvs?.map((orv, index) => [
					isFirstIndex(index) ? t('tabulka.dokumenty-historie.cislo-orv') : '',
					orv?.cisloOrv?.xVal,
				])
		),
	};
};
